/**
 * useSubmitTask — calls complete-task with action='submit'.
 *
 * Surfaces the Edge Function's structured error codes (INCOMPLETE_CHECKLIST,
 * MISSING_PHOTOS, INVALID_STATE, FORBIDDEN, RATE_LIMITED, etc.) as the
 * AppError.code so the UI can render an inline blocking error per
 * CLAUDE.md ERROR 4 (do not swallow, do not toast — block at the route).
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useSupabase }                                          from './useSupabase'
import { AppError }                                             from '@/lib/errors/AppError'

interface SubmitVars {
  taskId:                 string
  actualMinutes?:         number
  /** Operational counter — number of full trash bags carried out. Integer 0–10. #74a. */
  trashBagCount?:         number
  /** Operational counter — number of large appliances cleaned. Integer 0–10. #74a. */
  appliancesCleanedCount?: number
}

export interface SubmitResult {
  task_id:                  string
  task_status:              'completed'
  room_next_status:         'inspection_pending' | 'ready'
  room: {
    room_id:         string
    previous_status: string
    new_status:      string
  }
  trash_bag_count:          number
  appliances_cleaned_count: number
}

export function useSubmitTask(): UseMutationResult<SubmitResult, AppError, SubmitVars> {
  const supabase    = useSupabase()
  const queryClient = useQueryClient()

  return useMutation<SubmitResult, AppError, SubmitVars>({
    mutationFn: async ({ taskId, actualMinutes, trashBagCount, appliancesCleanedCount }): Promise<SubmitResult> => {
      const { data, error } = await supabase.functions.invoke<SubmitResult>('complete-task', {
        body: {
          task_id:                  taskId,
          action:                   'submit',
          actual_minutes:           actualMinutes,
          trash_bag_count:          trashBagCount,
          appliances_cleaned_count: appliancesCleanedCount,
        },
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      })
      if (error) {
        // Pull error code/detail out of the function response body.
        // supabase-js sets `context` to the Response object on a non-2xx
        // (FunctionsHttpError) — its body is a ReadableStream and must be
        // read via .clone().json(), NOT treated as a string. Reading it as
        // a string silently fails the parse and strands the cleaner on the
        // generic "non-2xx" message instead of the real block reason
        // (MISSING_PHOTOS, PHOTO_DEFECTS_FOUND, …). See useSignInWithPin.
        const ctx  = error as unknown as { context?: Response }
        let code   = 'SUBMIT_FAILED'
        let detail = error.message
        try {
          const parsed = await ctx.context?.clone().json() as { error?: string; detail?: string }
          if (parsed?.error)  code   = parsed.error
          if (parsed?.detail) detail = parsed.detail
        } catch { /* response was not JSON */ }
        throw new AppError({
          code,
          message:     detail,
          userMessage: userMessageFor(code, detail),
          status:      500,
        })
      }
      if (!data) {
        throw new AppError({
          code: 'EMPTY_RESPONSE', message: 'No data', userMessage: 'Submit returned no result.', status: 500,
        })
      }
      return data
    },

    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ['task',                vars.taskId] })
      void queryClient.invalidateQueries({ queryKey: ['my-tasks']                          })
      void queryClient.invalidateQueries({ queryKey: ['cleaning-assistant',  vars.taskId] })
    },
  })
}

function userMessageFor(code: string, detail: string): string {
  switch (code) {
    case 'INCOMPLETE_CHECKLIST':         return 'Some required checklist items are not yet complete.'
    case 'MISSING_PHOTOS':               return 'One or more required photos have not been uploaded.'
    case 'PHOTO_DEFECTS_FOUND':          return 'The ReadyScan inspection found cleaning issues that must be re-cleaned before submitting.'
    case 'INVALID_STATE':                return 'This task is not in a state that can be submitted.'
    case 'FORBIDDEN_ROLE':               return 'Your role is not allowed to submit this task.'
    case 'FORBIDDEN_OWNERSHIP':          return 'You can only submit tasks assigned to you.'
    case 'FORBIDDEN_ORG':                return 'This task does not belong to your organization.'
    case 'RATE_LIMITED':                 return 'Too many submissions in a row. Try again in a moment.'
    case 'BLOCKING_MAINTENANCE':         return 'There are unresolved blocking maintenance issues for this room.'
    case 'SUPERVISOR_APPROVAL_REQUIRED': return 'A supervisor must approve this task before it can move forward.'
    case 'INSPECTION_REQUIRED':          return 'An inspection is required before this room can be marked ready.'
    case 'TASK_NOT_FOUND':               return 'This task could not be found.'
    case 'INVALID_COUNT':                return 'Operational counters must be whole numbers between 0 and 10.'
    case 'INVALID_TRASH_BAG_COUNT':      return 'Trash bag count must be a whole number between 0 and 10.'
    case 'INVALID_APPLIANCES_COUNT':     return 'Appliance count must be a whole number between 0 and 10.'
    case 'EXCEEDS_TIER_LIMIT':                  return 'The trash or appliance counts exceed the limit for this task’s service tier. Talk to your supervisor before submitting.'
    case 'EXCEEDS_TIER_LIMIT_NEEDS_FEE_REQUEST':return 'The trash or appliance counts exceed the tier limit. File an additional-fee request first, then submit again.'
    default:                             return detail || 'Could not submit the task.'
  }
}
